import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { API_ENDPOINTS } from '../config/api';

const StaffForcePasswordChange = () => {
  const navigate = useNavigate();
  const staff = JSON.parse(localStorage.getItem('tempStaffUser') || '{}');

  const [passwordForm, setPasswordForm] = useState({
    newPassword: '',
    confirmPassword: ''
  });
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);

  if (!staff || !staff.email) {
    navigate('/staff-login');
    return null;
  }

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setPasswordForm(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handlePasswordChange = async (e) => {
    e.preventDefault();

    // Reset messages
    setError('');
    setSuccess('');

    // Validation
    if (!passwordForm.newPassword || !passwordForm.confirmPassword) {
      setError('Both password fields are required');
      return;
    }

    if (passwordForm.newPassword.length < 6) {
      setError('New password must be at least 6 characters long');
      return;
    }

    if (passwordForm.newPassword !== passwordForm.confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    setLoading(true);

    try {
      console.log('Security Client: Attempting password change for:', staff.email);

      const requestBody = {
        email: staff.email,
        currentPassword: 'staff123', // Default password
        newPassword: passwordForm.newPassword
      };

      console.log('Share Client: Sending request:', {
        url: `${API_ENDPOINTS.STAFF}/change-password`,
        body: { ...requestBody, newPassword: '[HIDDEN]' }
      });

      const response = await fetch(`${API_ENDPOINTS.STAFF}/change-password`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(requestBody)
      });

      console.log('📥 Client: Response status:', response.status);
      console.log('📥 Client: Response ok:', response.ok);

      const result = await response.json();
      console.log('📥 Client: Response data:', result);

      if (response.ok) {
        console.log('Loading Client: Password change successful');
        setSuccess('Password changed successfully! Redirecting...');

        // Update staff object with passwordChanged flag
        const updatedStaff = {
          ...staff,
          passwordChanged: true
        };

        // Clear temp data and store updated staff
        localStorage.removeItem('tempStaffUser');
        localStorage.setItem('staffUser', JSON.stringify(updatedStaff));

        setTimeout(() => {
          navigate('/staff-dashboard');
        }, 2000);
      } else {
        console.log('X Client: Password change failed:', result.error);
        setError(result.error || 'Failed to change password');
      }
    } catch (error) {
      console.error('X Client: Network error:', error);
      setError('Failed to change password. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-green-50 to-emerald-100 flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        <div className="text-center">
          <div className="mx-auto h-16 w-16 bg-orange-500 rounded-full flex items-center justify-center">
            <svg className="h-8 w-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
            </svg>
          </div>
          <h2 className="mt-6 text-3xl font-extrabold text-gray-900">
            Set New Password
          </h2>
          <p className="mt-2 text-sm text-gray-600">
            Welcome! You must create a new secure password to access your staff dashboard.
          </p>
        </div>

        <div className="bg-white py-8 px-6 shadow-lg rounded-lg">
          {error && (
            <div className="mb-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded">
              {error}
            </div>
          )}

          {success && (
            <div className="mb-4 p-3 bg-green-100 border border-green-400 text-green-700 rounded">
              {success}
            </div>
          )}

          <form onSubmit={handlePasswordChange}>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  New Password *
                </label>
                <input
                  type="password"
                  name="newPassword"
                  value={passwordForm.newPassword}
                  onChange={handleFormChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
                  placeholder="Enter new password (min 6 characters)"
                  required
                  disabled={loading}
                />
                <p className="text-xs text-gray-500 mt-1">
                  Password must be at least 6 characters long
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Confirm New Password *
                </label>
                <input
                  type="password"
                  name="confirmPassword"
                  value={passwordForm.confirmPassword}
                  onChange={handleFormChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
                  placeholder="Confirm new password"
                  required
                  disabled={loading}
                />
              </div>
            </div>

            <div className="mt-6">
              <button
                type="submit"
                disabled={loading}
                className="w-full bg-green-600 text-white py-3 px-4 rounded-md hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2 transition duration-200 font-medium disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? 'Setting Password...' : 'Set New Password'}
              </button>
            </div>
          </form>

          {/* Security Notice */}
          <div className="mt-6 p-3 bg-green-50 border border-green-200 rounded-md">
            <div className="flex items-start">
              <div className="text-green-600 text-sm">Locked</div>
              <div className="ml-2">
                <p className="text-sm text-green-800 font-medium">Security Notice</p>
                <p className="text-xs text-green-600 mt-1">
                  Choose a strong password that includes letters, numbers, and special characters.
                  Do not share your password with anyone.
                </p>
              </div>
            </div>
          </div>

          {/* Back to Login */}
          <div className="mt-4 text-center">
            <button
              onClick={() => navigate('/staff-login')}
              className="text-sm text-gray-500 hover:text-gray-700 underline"
              disabled={loading}
            >
              Back to Login
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default StaffForcePasswordChange;
